// Lightweight framer-motion-compatible subset built on native Web Animations (CSS engine).
// Supports: initial/animate/exit/transition, variants (+stagger), whileHover/whileTap/whileInView,
// keyframe arrays, repeat, height/width 'auto', AnimatePresence (sync | wait | popLayout).
import {
  Children, createContext, createElement, forwardRef, isValidElement, useCallback,
  useContext, useEffect, useLayoutEffect, useReducer, useRef, useState,
} from 'react';

const TF = ['x', 'y', 'z', 'scale', 'scaleX', 'scaleY', 'rotate', 'rotateX', 'rotateY', 'rotateZ', 'skewX', 'skewY'];
const TF_FN = { x: 'translateX', y: 'translateY', z: 'translateZ' };
const DEFAULTS = { x: 0, y: 0, z: 0, scale: 1, scaleX: 1, scaleY: 1, rotate: 0, rotateX: 0, rotateY: 0, rotateZ: 0, skewX: 0, skewY: 0 };
const PX = new Set(['width', 'height', 'top', 'left', 'right', 'bottom', 'borderRadius', 'minHeight', 'maxHeight', 'minWidth', 'maxWidth', 'marginTop', 'marginBottom', 'paddingTop', 'paddingBottom']);
const EASE = {
  linear: 'linear', easeIn: 'cubic-bezier(0.42,0,1,1)', easeOut: 'cubic-bezier(0,0,0.58,1)',
  easeInOut: 'cubic-bezier(0.42,0,0.58,1)', circIn: 'cubic-bezier(0.55,0,1,0.45)',
  circOut: 'cubic-bezier(0,0.55,0.45,1)', circInOut: 'cubic-bezier(0.85,0,0.15,1)',
  backIn: 'cubic-bezier(0.36,0,0.66,-0.56)', backOut: 'cubic-bezier(0.34,1.56,0.64,1)',
  backInOut: 'cubic-bezier(0.68,-0.6,0.32,1.6)', anticipate: 'cubic-bezier(0.36,0,0.66,-0.56)',
};
const SPRING = 'cubic-bezier(0.25,1,0.5,1)';
const BASE = Symbol('base');
const OMIT = ['layout', 'layoutId', 'layoutScroll', 'drag', 'dragConstraints', 'dragElastic', 'dragMomentum',
  'dragListener', 'dragSnapToOrigin', 'onDrag', 'onDragStart', 'onDragEnd', 'whileDrag', 'whileFocus',
  'onAnimationStart', 'onAnimationComplete', 'onUpdate', 'onHoverStart', 'onHoverEnd', 'onTap', 'onTapStart',
  'transformTemplate', 'onViewportEnter', 'onViewportLeave'];

const isTf = (k) => k in DEFAULTS;
const last = (v) => (Array.isArray(v) ? v[v.length - 1] : v);
const tfUnit = (k, v) => {
  if (typeof v !== 'number') return v;
  if (k.startsWith('scale')) return v;
  return k.startsWith('rotate') || k.startsWith('skew') ? `${v}deg` : `${v}px`;
};
const toTransform = (vals) => TF.filter((k) => vals[k] !== undefined && vals[k] !== DEFAULTS[k])
  .map((k) => `${TF_FN[k] || k}(${tfUnit(k, vals[k])})`).join(' ') || 'none';
const cssVal = (k, v) => (typeof v === 'number' && PX.has(k) ? `${v}px` : v);
const strip = (t) => { if (!t) return {}; const { transition, transitionEnd, ...v } = t; return v; };

function toStyle(values) {
  const out = {};
  let tf = false;
  Object.entries(values).forEach(([k, v]) => { if (isTf(k)) tf = true; else out[k] = cssVal(k, last(v)); });
  if (tf) out.transform = toTransform(Object.fromEntries(Object.entries(values).map(([k, v]) => [k, last(v)])));
  return out;
}

function resolveDef(def, variants, custom) {
  if (def == null || def === false) return undefined;
  if (typeof def !== 'string' && !Array.isArray(def)) return def;
  return (Array.isArray(def) ? def : [def]).reduce((acc, label) => {
    let v = variants?.[label];
    if (typeof v === 'function') v = v(custom);
    return v ? { ...acc, ...v } : acc;
  }, undefined);
}

const prefersReduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function currentValue(el, st, k, cs, wasRunning) {
  const v = st.vals[k];
  if ((k === 'height' || k === 'width') && (wasRunning || v === undefined || v === 'auto')) return `${k === 'height' ? el.offsetHeight : el.offsetWidth}px`;
  return v === undefined || wasRunning ? cs[k] : cssVal(k, v);
}

function resolveAuto(el, k, v) {
  if (v !== 'auto' || (k !== 'height' && k !== 'width')) return cssVal(k, v);
  return `${k === 'height' ? el.scrollHeight : el.scrollWidth}px`;
}

function timing(t, hasTf, hasArray) {
  const spring = t.type === 'spring' || (t.type !== 'tween' && t.duration === undefined && t.ease === undefined && hasTf && !hasArray);
  const ease = Array.isArray(t.ease) ? `cubic-bezier(${t.ease.join(',')})` : EASE[t.ease];
  return {
    duration: prefersReduced() ? 0 : (t.duration ?? (spring ? 0.45 : 0.3)) * 1000,
    delay: prefersReduced() ? 0 : (t.delay || 0) * 1000,
    easing: spring ? SPRING : ease || (hasArray ? EASE.easeInOut : EASE.easeOut),
    iterations: t.repeat === Infinity ? Infinity : (t.repeat || 0) + 1,
    direction: t.repeatType === 'reverse' || t.repeatType === 'mirror' ? 'alternate' : 'normal',
  };
}

function isSettled(st, target) {
  return Object.entries(target).every(([k, v]) => !Array.isArray(v)
    && (isTf(k) ? (st.vals[k] ?? DEFAULTS[k]) === v : st.vals[k] === v));
}

function animateTo(el, st, rawTarget, t = {}, transitionEnd) {
  const keys = Object.keys(rawTarget);
  if (!el || !keys.length) return Promise.resolve();
  let cs = getComputedStyle(el);
  const target = {};
  keys.forEach((k) => {
    if (!isTf(k) && !(k in st.base)) st.base[k] = st.vals[k] ?? cs[k];
    target[k] = rawTarget[k] === BASE ? (isTf(k) ? DEFAULTS[k] : st.base[k]) : rawTarget[k];
  });
  if (isSettled(st, target)) return st.promise || Promise.resolve();
  const wasRunning = !!st.anim;
  if (st.anim) {
    try { st.anim.commitStyles(); } catch (e) { /* detached */ }
    st.anim.cancel();
    st.anim = null;
    cs = getComputedStyle(el);
  }
  const hasArray = keys.some((k) => Array.isArray(target[k]));
  const len = Math.max(2, ...keys.map((k) => (Array.isArray(target[k]) ? target[k].length : 0)));
  const frames = Array.from({ length: len }, () => ({}));
  const tfFrames = Array.from({ length: len }, () => ({ ...st.vals }));
  let hasTf = false;
  keys.forEach((k) => {
    const seq = Array.isArray(target[k]) ? [...target[k]] : [null, ...Array(len - 1).fill(target[k])];
    if (seq[0] == null) seq[0] = isTf(k) ? (st.vals[k] ?? DEFAULTS[k]) : currentValue(el, st, k, cs, wasRunning);
    if (isTf(k)) { hasTf = true; seq.forEach((v, i) => { tfFrames[i][k] = v; }); } else {
      seq.forEach((v, i) => { frames[i][k] = resolveAuto(el, k, v); });
    }
    st.vals[k] = last(target[k]);
  });
  if (hasTf) {
    tfFrames.forEach((f, i) => { frames[i].transform = toTransform(f); });
    if (wasRunning && cs.transform && cs.transform !== 'none') frames[0].transform = cs.transform;
  }
  const finalStyle = { ...frames[len - 1] };
  keys.forEach((k) => { if (target[k] === 'auto') finalStyle[k] = 'auto'; });
  const commit = () => { Object.assign(el.style, finalStyle, transitionEnd || {}); };
  const opts = timing(t, hasTf, hasArray);
  if (!el.animate || (opts.duration === 0 && opts.delay === 0)) { commit(); st.promise = null; return Promise.resolve(); }
  const anim = el.animate(frames, { ...opts, fill: 'both' });
  st.anim = anim;
  st.promise = anim.finished.then(() => {
    if (st.anim !== anim) return;
    commit();
    anim.cancel();
    st.anim = null;
    st.promise = null;
  }).catch(() => {});
  return st.promise;
}

const PresenceCtx = createContext(null);
const VariantCtx = createContext(null);

function useGestures(props, setHover, setTap) {
  const { onPointerEnter, onPointerLeave, onPointerDown } = props;
  return {
    onPointerEnter: props.whileHover ? (e) => { if (e.pointerType !== 'touch') setHover(true); onPointerEnter?.(e); } : onPointerEnter,
    onPointerLeave: props.whileHover ? (e) => { setHover(false); onPointerLeave?.(e); } : onPointerLeave,
    onPointerDown: props.whileTap ? (e) => {
      setTap(true);
      const up = () => { setTap(false); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); };
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
      onPointerDown?.(e);
    } : onPointerDown,
  };
}

function useInViewState(elRef, enabled, viewport) {
  const [inView, setInView] = useState(false);
  const once = viewport?.once;
  const margin = viewport?.margin;
  const amount = viewport?.amount;
  useEffect(() => {
    const el = elRef.current;
    if (!enabled || !el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); if (once) io.disconnect(); } else if (!once) setInView(false);
    }, { rootMargin: margin || '0px', threshold: amount === 'all' ? 1 : typeof amount === 'number' ? amount : 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [elRef, enabled, once, margin, amount]);
  return inView;
}

function buildTarget({ initialT, animateT, viewT, hoverT, tapT, inView, hover, tap }) {
  const target = {};
  [viewT, hoverT, tapT].forEach((layer) => {
    Object.keys(strip(layer)).forEach((k) => {
      if (animateT && k in animateT) target[k] = animateT[k];
      else if (initialT && k in initialT) target[k] = initialT[k];
      else target[k] = BASE;
    });
  });
  let transition;
  [animateT, inView && viewT, hover && hoverT, tap && tapT].forEach((layer) => {
    if (!layer) return;
    Object.assign(target, strip(layer));
    if (layer.transition) transition = layer.transition;
  });
  return { target, transition, transitionEnd: animateT?.transitionEnd };
}

function useMotion(props, ref) {
  const {
    initial, animate, exit, transition, variants, whileHover, whileTap, whileInView, viewport, custom, style, children,
  } = props;
  const presence = useContext(PresenceCtx);
  const parentV = useContext(VariantCtx);
  const inherit = !!variants && !!parentV;
  const initialDef = initial !== undefined ? initial : inherit ? parentV.initial : undefined;
  const animateDef = animate !== undefined ? animate : inherit ? parentV.animate : undefined;
  const [index] = useState(() => (inherit ? parentV.next() : 0));
  const elRef = useRef(null);
  const st = useRef({ vals: {}, base: {}, anim: null }).current;
  const [hover, setHover] = useState(false);
  const [tap, setTap] = useState(false);
  const idRef = useRef(Symbol('motion'));

  const initialT = resolveDef(initialDef, variants, custom);
  const animateT = resolveDef(animateDef, variants, custom);
  const viewT = resolveDef(whileInView, variants, custom);
  const hoverT = resolveDef(whileHover, variants, custom);
  const tapT = resolveDef(whileTap, variants, custom);
  const exitT = resolveDef(exit, variants, custom);
  const inView = useInViewState(elRef, !!viewT, viewport);

  const [initialStyle] = useState(() => {
    const base = initialDef === undefined || initialDef === false ? animateT : initialT;
    if (!base) return null;
    const v = strip(base);
    Object.entries(v).forEach(([k, val]) => { st.vals[k] = Array.isArray(val) ? val[0] : val; });
    return toStyle(Object.fromEntries(Object.entries(v).map(([k, val]) => [k, Array.isArray(val) ? val[0] : val])));
  });

  const exiting = !!(presence && !presence.isPresent && exitT);
  const built = exiting
    ? { target: strip(exitT), transition: exitT.transition }
    : buildTarget({ initialT, animateT, viewT, hoverT, tapT, inView, hover, tap });
  let t = { ...(transition || {}), ...(built.transition || {}) };
  if (inherit && parentV.stagger !== undefined && !hover && !tap) {
    t = { ...t, delay: (t.delay || 0) + parentV.delayChildren + index * parentV.stagger };
  }
  const key = JSON.stringify(built.target, (k, v) => (v === BASE ? '__base' : v)) + (exiting ? '|exit' : '');
  const latest = useRef(null);
  latest.current = { target: built.target, t, end: built.transitionEnd };
  const register = presence?.register;

  useLayoutEffect(() => (exitT && register ? register(idRef.current) : undefined), [!!exitT, register]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { target, t: tr, end } = latest.current;
    const p = animateTo(elRef.current, st, target, tr, end);
    if (exiting) p.then(() => presence.done(idRef.current));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRef = useCallback((node) => {
    elRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const activeLabel = inView && typeof whileInView === 'string' ? whileInView : animateDef;
  const vctx = variants ? {
    initial: initialDef,
    animate: activeLabel,
    stagger: variants[activeLabel]?.transition?.staggerChildren,
    delayChildren: variants[activeLabel]?.transition?.delayChildren || 0,
  } : null;
  const counterRef = useRef(0);
  if (vctx) vctx.next = () => counterRef.current++;

  return {
    setRef,
    style: initialStyle ? { ...style, ...initialStyle } : style,
    gestures: useGestures(props, setHover, setTap),
    children: vctx ? createElement(VariantCtx.Provider, { value: vctx }, children) : children,
  };
}

const MOTION_KEYS = ['initial', 'animate', 'exit', 'transition', 'variants', 'whileHover', 'whileTap', 'whileInView', 'viewport', 'custom', 'style', 'children', ...OMIT];

function createMotion(tag) {
  const Comp = forwardRef((props, ref) => {
    const m = useMotion(props, ref);
    const domProps = {};
    Object.keys(props).forEach((k) => { if (!MOTION_KEYS.includes(k)) domProps[k] = props[k]; });
    return createElement(tag, { ...domProps, ...m.gestures, ref: m.setRef, style: m.style }, m.children);
  });
  Comp.displayName = `motion.${typeof tag === 'string' ? tag : tag.displayName || 'component'}`;
  return Comp;
}

const cache = new Map();
const factory = (tag) => { if (!cache.has(tag)) cache.set(tag, createMotion(tag)); return cache.get(tag); };
export const motion = new Proxy(factory, { get: (_, tag) => factory(tag) });

function PresenceChild({ isPresent, onExitComplete, children }) {
  const registered = useRef(new Set());
  const pending = useRef(null);
  const completed = useRef(false);
  const doneRef = useRef(onExitComplete);
  doneRef.current = onExitComplete;
  const check = () => {
    if (pending.current && pending.current.size === 0) { pending.current = null; completed.current = true; doneRef.current(); }
  };
  const [ctx] = useState(() => ({
    register: (id) => { registered.current.add(id); return () => { registered.current.delete(id); pending.current?.delete(id); check(); }; },
    done: (id) => { pending.current?.delete(id); check(); },
  }));
  if (!isPresent && !pending.current && !completed.current) pending.current = new Set(registered.current);
  if (isPresent) { pending.current = null; completed.current = false; }
  useEffect(() => { if (!isPresent) check(); });
  const value = useRef({});
  if (value.current.isPresent !== isPresent) value.current = { ...ctx, isPresent };
  return createElement(PresenceCtx.Provider, { value: value.current }, children);
}

export function AnimatePresence({ children, mode = 'sync' }) {
  const [, force] = useReducer((x) => x + 1, 0);
  const exiting = useRef(new Map());
  const prev = useRef([]);
  const present = Children.toArray(children).filter(isValidElement);
  const presentKeys = new Set(present.map((c) => c.key));
  prev.current.forEach((el, i) => {
    if (!presentKeys.has(el.key) && !exiting.current.has(el.key)) exiting.current.set(el.key, { el, index: i });
  });
  presentKeys.forEach((k) => exiting.current.delete(k));
  const prevKeys = new Set(prev.current.map((e) => e.key));
  let out = mode === 'wait' && exiting.current.size ? present.filter((c) => prevKeys.has(c.key)) : [...present];
  exiting.current.forEach(({ el, index }) => { out.splice(Math.min(index, out.length), 0, el); });
  prev.current = out;
  const finish = (key) => {
    exiting.current.delete(key);
    prev.current = prev.current.filter((e) => e.key !== key);
    force();
  };
  out = out.map((el) => createElement(PresenceChild, {
    key: el.key, isPresent: !exiting.current.has(el.key), onExitComplete: () => finish(el.key),
  }, el));
  return out;
}
